import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, ArrowRight, Clock, ListChecks, Coin, Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import { categoryVisual, humaniseSlug, usePublicExamCategories } from "../lib/examCategories";

export default function Browse() {
  const { category: slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { items: allCats } = usePublicExamCategories();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/public/exams${slug ? `?category=${encodeURIComponent(slug)}` : ""}`)
      .then((r) => setExams(r.data || []))
      .catch(() => setExams([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const label = useMemo(() => {
    if (!slug) return "All exams";
    const c = allCats.find((x) => x.slug === slug);
    return c?.label || humaniseSlug(slug);
  }, [slug, allCats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return exams;
    return exams.filter(
      (e) =>
        (e.title || "").toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q) ||
        (e.institution_name || "").toLowerCase().includes(q),
    );
  }, [exams, search]);

  const { Icon, tone } = categoryVisual(slug);

  const openExam = (eid) => {
    if (user) {
      navigate(`/app/exam/${eid}`);
    } else {
      navigate(`/login?next=${encodeURIComponent(`/app/exam/${eid}`)}`);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="browse-page">
      {/* Top bar */}
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="browse-home">
            <div className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/pricing" className="hidden sm:inline-block text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="browse-nav-pricing">Pricing</Link>
            {user ? (
              <Link to="/app" className="text-sm font-medium px-4 py-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors" data-testid="browse-open-app">
                Open dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium px-4 py-2 text-zinc-700 hover:text-zinc-900" data-testid="browse-login">Sign in</Link>
                <Link to="/register" className="text-sm font-medium px-4 py-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors" data-testid="browse-register">
                  Get started
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Category hero */}
      <section className={`border-b border-zinc-200 ${tone.bg}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-4" data-testid="browse-back">
            <ArrowLeft size={12} /> Back to home
          </Link>
          <div className="flex items-start gap-5">
            <div className={`w-14 h-14 flex items-center justify-center border ${tone.ring} ${tone.iconBg} shrink-0`}>
              <Icon size={26} weight="regular" className={tone.iconFg} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1 font-semibold">Browse category</div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="browse-title">
                {label}
              </h1>
              <div className="mt-2 text-sm text-zinc-600">
                {loading ? "Loading exams…" : `${exams.length} published exam${exams.length === 1 ? "" : "s"} in this category.`}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search exams in this category…"
              className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="browse-search-input"
            />
          </div>
          {allCats.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="browse-other-cats">
              {allCats
                .filter((c) => c.slug !== slug)
                .slice(0, 6)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/browse/${c.slug}`}
                    className="text-[11px] uppercase tracking-wider border border-zinc-300 text-zinc-700 hover:border-zinc-900 px-3 py-1.5"
                    data-testid={`browse-jump-${c.slug}`}
                  >
                    {c.label} <span className="ml-1 font-mono opacity-60">{c.count}</span>
                  </Link>
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Exam list */}
      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-zinc-200 p-12 text-center" data-testid="browse-empty">
              <div className="font-display text-2xl font-bold mb-2">No exams here yet</div>
              <div className="text-sm text-zinc-500 mb-6">
                {search ? "Try a different search term." : `There are no published exams under "${label}" right now.`}
              </div>
              <Link to="/" className="inline-flex items-center gap-2 border border-zinc-900 px-5 py-2.5 text-sm font-medium hover:bg-zinc-900 hover:text-white transition-colors">
                <ArrowLeft size={12} /> Back to home
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="browse-exams-grid">
              {filtered.map((e) => (
                <ExamCard key={e.exam_id} exam={e} onOpen={() => openExam(e.exam_id)} authed={!!user} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Guest CTA */}
      {!user && (
        <section className="border-t border-zinc-200 bg-zinc-950 text-zinc-100" data-testid="browse-guest-cta">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-semibold mb-1">Ready to attempt?</div>
              <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Sign up free to start taking exams.</div>
              <div className="text-sm text-zinc-400 mt-1">No credit card required. WhatsApp demo on request.</div>
            </div>
            <div className="flex gap-2">
              <Link to="/register" className="inline-flex items-center gap-2 bg-amber-400 text-zinc-950 px-5 py-3 text-sm font-bold hover:bg-amber-300 transition-colors" data-testid="browse-cta-register">
                Create account <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-100 px-5 py-3 text-sm font-semibold hover:border-amber-400 hover:text-amber-400 transition-colors" data-testid="browse-cta-login">
                Sign in
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function ExamCard({ exam, onOpen, authed }) {
  const { Icon, tone } = categoryVisual(exam.category);
  const price = Number(exam.price_credits || 0);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left border border-zinc-200 bg-white hover:border-zinc-900 transition-colors p-5 flex flex-col group"
      data-testid={`browse-exam-${exam.exam_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className={`w-10 h-10 flex items-center justify-center border ${tone.ring} ${tone.iconBg}`}>
          <Icon size={18} weight="regular" className={tone.iconFg} />
        </div>
        {price > 0 ? (
          <span className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
            <Coin size={10} weight="fill" className="text-amber-500" /> {price} coins
          </span>
        ) : (
          <span className="inline-block border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
            Free
          </span>
        )}
      </div>
      <div className="font-display text-lg font-bold leading-snug line-clamp-2 mb-1">{exam.title}</div>
      {exam.description && <div className="text-xs text-zinc-500 line-clamp-2 mb-4">{exam.description}</div>}
      <div className="mt-auto border-t border-zinc-100 pt-3 flex items-center justify-between text-[11px] text-zinc-600">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {exam.duration_minutes}m</span>
          <span className="inline-flex items-center gap-1"><ListChecks size={11} /> {exam.question_count} Q</span>
        </div>
        {exam.institution_name && (
          <span className="inline-flex items-center gap-1 text-zinc-500 truncate max-w-[10rem]">
            <Buildings size={10} /> {exam.institution_name}
          </span>
        )}
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-wider font-semibold text-zinc-900 inline-flex items-center gap-1 opacity-70 group-hover:opacity-100">
        {authed ? "Open exam" : "Sign in to attempt"} <ArrowRight size={11} weight="bold" />
      </div>
    </button>
  );
}
